import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import MathText from '@/components/MathText';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PracticeResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { sessionId, total_questions, score, score_percentage, results, mode, topic, returnPath } = location.state || {};
  const backPath = returnPath || '/practice';  const correct_count = score;
  const accuracy = score_percentage;
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExplanations, setShowExplanations] = useState({});
  const [startingNewPractice, setStartingNewPractice] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      navigate(backPath);
      return;
    }
    
    // Only fetch if we don't have results in state
    if (!results) {
      fetchSessionDetails();
    }
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/practice/sessions/${sessionId}`);
      setSession(response.data);
    } catch (error) {
      console.error('Error fetching session:', error);
      // Don't show error toast - we have enough data from state
    } finally {
      setLoading(false);
    }
  };

  const handlePracticeAgain = async () => {
    if (!mode) {
      // If we don't have the mode, just go back to practice page
      navigate(backPath);
      return;
    }

    try {
      setStartingNewPractice(true);
      
      // Start a new practice session with the same settings
      const response = await axios.post(`${API}/practice/sessions/start`, {
        topic: topic || 'all',
        mode: mode,
        count: 10
      });

      // Navigate to the practice session with new questions
      navigate('/practice/session', {
        state: {
          sessionId: response.data.session_id,
          questions: response.data.questions,
          mode: mode,
          topic: topic || 'all',
          returnPath: backPath
        }
      });
    } catch (error) {
      console.error('Error starting new practice:', error);
      toast.error('Failed to start new practice');
      navigate(backPath);
    } finally {
      setStartingNewPractice(false);
    }
  };

  const toggleExplanation = (questionId) => {
    setShowExplanations({
      ...showExplanations,
      [questionId]: !showExplanations[questionId]
    });
  };

  const getScoreColor = (acc) => {
    if (acc >= 80) return '#10b981';
    if (acc >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreEmoji = (acc) => {
    if (acc >= 80) return '🎉';
    if (acc >= 60) return '👍';
    return '💪';
  };

  // Back button for header - goes to dashboard
  const backButton = (
    <Button 
      onClick={() => navigate(backPath)}
      variant="outline"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none'
      }}
    >
      Back
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        logoClickPath="/dashboard"
        rightContent={backButton}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div className="results-content-container" style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
          {/* Title */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', marginBottom: '1rem', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            Practice Results
          </h1>

          {/* Results Card */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                {getScoreEmoji(accuracy || 0)}
              </div>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', color: '#1a202c' }}>
                Practice Complete!
              </h2>
            </div>

            {/* Score Summary */}
            <div className="score-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
              <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1a202c', marginBottom: '0.5rem' }}>
                  {total_questions || 0}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Total Questions</div>
              </div>
              <div style={{ background: '#d1fae5', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
                  {correct_count || 0}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Correct</div>
              </div>
              <div style={{ background: '#e0e7ff', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: getScoreColor(accuracy || 0), marginBottom: '0.5rem' }}>
                  {accuracy ? accuracy.toFixed(1) : 0}%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Accuracy</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Button
                onClick={handlePracticeAgain}
                disabled={startingNewPractice}
                style={{
                  background: '#0030b3',
                  color: 'white',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  opacity: startingNewPractice ? 0.6 : 1,
                  cursor: startingNewPractice ? 'not-allowed' : 'pointer'
                }}
              >
                {startingNewPractice ? 'Starting...' : 'Practice Again'}
              </Button>
              <Button
                onClick={() => navigate('/practice')}
                variant="outline"
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem'
                }}
              >
                Go to Practice
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                data-testid="go-to-dashboard-btn"
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem'
                }}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>

          {/* Question Review (if results available) */}
          {results && results.length > 0 && (
            <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', fontFamily: 'Arial, sans-serif' }}>
                Question Review & Explanations
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {results.map((result, idx) => (
                  <div 
                    key={result.question_id} 
                    style={{ 
                      padding: '1.5rem', 
                      borderRadius: '8px', 
                      border: `2px solid ${result.is_correct ? '#10b981' : '#ef4444'}`,
                      background: 'white'
                    }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: '600', fontFamily: 'Arial, sans-serif' }}>Question {idx + 1}</span>
                      <span style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '9999px', 
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        background: result.is_correct ? '#10b981' : '#ef4444',
                        color: 'white'
                      }}>
                        {result.is_correct ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    </div>

                    {/* Question Text */}
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                        <MathText>{result.question_text}</MathText>
                      </div>

                      {/* Image if present */}
                      {(result.image_url || result.image) && (
                        <img 
                          src={result.image_url || result.image} 
                          alt="Question diagram" 
                          style={{ 
                            maxWidth: 'min(400px, 100%)', 
                            width: 'auto',
                            height: 'auto',
                            marginBottom: '1rem', 
                            borderRadius: '4px',
                            display: 'block'
                          }}
                        />
                      )}
                    </div>

                    {/* Answer Choices (if applicable) */}
                    {result.choices && result.choice_texts && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {result.choices.map((choice) => {
                            const isUserAnswer = result.user_answer === choice;
                            const isCorrectAnswer = result.correct_answer === choice;
                            
                            let borderColor = '#e5e7eb';
                            let backgroundColor = 'white';
                            
                            if (isCorrectAnswer) {
                              borderColor = '#10b981';
                              backgroundColor = '#f0fdf4';
                            } else if (isUserAnswer && !result.is_correct) {
                              borderColor = '#ef4444';
                              backgroundColor = '#fef2f2';
                            }
                            
                            return (
                              <div
                                key={choice}
                                style={{
                                  padding: '0.75rem',
                                  borderRadius: '6px',
                                  border: `2px solid ${borderColor}`,
                                  background: backgroundColor,
                                  display: 'flex',
                                  alignItems: 'flex-start'
                                }}
                              >
                                <span style={{ fontWeight: '600', marginRight: '0.75rem', color: '#333' }}>{choice}</span>
                                <div style={{ flex: 1 }}>
                                  <MathText>{result.choice_texts[choice]}</MathText>
                                </div>
                                {isCorrectAnswer && (
                                  <span style={{ marginLeft: '0.5rem', color: '#10b981', fontWeight: '600' }}>✓</span>
                                )}
                                {isUserAnswer && !result.is_correct && (
                                  <span style={{ marginLeft: '0.5rem', color: '#ef4444', fontWeight: '600' }}>✗</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Answer Summary */}
                    <div style={{ 
                      padding: '1rem', 
                      background: '#f9fafb', 
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Your answer:</strong> <span style={{ color: result.is_correct ? '#10b981' : '#ef4444', fontWeight: '600' }}>{result.user_answer || 'Not answered'}</span>
                      </div>
                      <div>
                        <strong>Correct answer:</strong> <span style={{ color: '#10b981', fontWeight: '600' }}>{result.correct_answer}</span>
                      </div>
                    </div>

                    {/* Explanation */}
                    {result.explanation && (
                      <div style={{ 
                        padding: '1.25rem', 
                        background: '#eff6ff', 
                        borderLeft: '4px solid #3b82f6',
                        borderRadius: '6px'
                      }}>
                        <h4 style={{ 
                          fontSize: '0.95rem', 
                          fontWeight: '600', 
                          marginBottom: '0.75rem',
                          color: '#1e40af',
                          fontFamily: 'Arial, sans-serif'
                        }}>
                          Explanation:
                        </h4>
                        <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#1e3a8a' }}>
                          <MathText>{result.explanation}</MathText>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PracticeResults;
